import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { nanoid } from 'nanoid';
import { supabase } from '@/utils/supabaseClient.js';

export const COOKIE_PREFERENCES_OPEN_EVENT = 'gameplaysync:open-cookie-preferences';

const CookieConsentContext = createContext(undefined);
CookieConsentContext.displayName = 'CookieConsentContext';

const STORAGE_KEY = 'gs_cookie_consent';
const SESSION_STORAGE_KEY = 'gs_cookie_session_id';
const normalizePolicyVersion = (value, fallback = '1.0.0') => {
  if (typeof value !== 'string') {
    return fallback;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const CONSENT_VERSION = normalizePolicyVersion(
  import.meta.env?.VITE_COMPLIANCE_POLICY_VERSION ||
    import.meta.env?.VITE_CONSENT_POLICY_VERSION ||
    import.meta.env?.VITE_COOKIE_CONSENT_VERSION ||
    '1.0.0',
);
const CATEGORY_KEYS = ['necessary', 'preferences', 'analytics', 'marketing'];
const SERVER_ENDPOINT = '/api/privacy/consent';
const DEFAULT_CHANNELS = ['banner'];
const DEFAULT_LEGAL_BASIS = ['consent'];
const DEFAULT_POLICY_SCOPES = ['privacy_policy', 'cookie_policy', 'terms_of_service'];
const DEFAULT_SYNC_SOURCE = 'cookie_preferences';

let inMemorySessionId = null;

const defaultConsent = {
  version: CONSENT_VERSION,
  categories: {
    necessary: true,
    preferences: false,
    analytics: false,
    marketing: false,
  },
  updatedAt: null,
  receiptId: null,
  sessionId: null,
  lastSyncedAt: null,
  channels: [...DEFAULT_CHANNELS],
  legalBasis: [...DEFAULT_LEGAL_BASIS],
  scopes: ['necessary'],
  record: null,
};

function normalizeConsentState(nextConsent, previous = defaultConsent) {
  const categories = normalizeCategories(nextConsent?.categories || previous.categories);
  const sessionId = ensureSessionId(nextConsent?.sessionId || previous.sessionId);
  const channels = mergeUniqueStrings(previous.channels || DEFAULT_CHANNELS, nextConsent?.channels, DEFAULT_CHANNELS);
  const legalBasis = mergeUniqueStrings(previous.legalBasis || DEFAULT_LEGAL_BASIS, nextConsent?.legalBasis, DEFAULT_LEGAL_BASIS);
  const scopes = mergeUniqueStrings(nextConsent?.scopes, buildScopesFromCategories(categories));

  return {
    ...previous,
    ...nextConsent,
    version: CONSENT_VERSION,
    categories,
    sessionId,
    channels,
    legalBasis,
    scopes,
  };
}

function normalizeCategories(input) {
  return {
    necessary: true,
    preferences: Boolean(input?.preferences),
    analytics: Boolean(input?.analytics),
    marketing: Boolean(input?.marketing),
  };
}

function areCategoriesEqual(a, b) {
  return CATEGORY_KEYS.every((key) => Boolean(a?.[key]) === Boolean(b?.[key]));
}

function categoriesObjectFromList(list) {
  if (!Array.isArray(list)) {
    return normalizeCategories(null);
  }

  return normalizeCategories(
    list.reduce((accumulator, key) => {
      if (CATEGORY_KEYS.includes(key)) {
        accumulator[key] = true;
      }
      return accumulator;
    }, {}),
  );
}

function normalizeStringArray(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter((entry) => entry.length > 0);
  }

  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }

  return [];
}

function mergeUniqueStrings(base = [], overlay = [], fallback = []) {
  const result = new Set();
  normalizeStringArray(fallback).forEach((value) => result.add(value));
  normalizeStringArray(base).forEach((value) => result.add(value));
  normalizeStringArray(overlay).forEach((value) => result.add(value));
  return Array.from(result);
}

function areStringArraysEqual(a = [], b = []) {
  const first = normalizeStringArray(a);
  const second = normalizeStringArray(b);
  if (first.length !== second.length) {
    return false;
  }
  const set = new Set(first);
  if (set.size !== second.length) {
    return false;
  }
  return second.every((value) => set.has(value));
}

function buildScopesFromCategories(categories) {
  const normalized = normalizeCategories(categories);
  return CATEGORY_KEYS.filter((key) => (key === 'necessary' ? true : Boolean(normalized?.[key])));
}

function createSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cc_${nanoid(21)}`;
}

function ensureSessionId(existing) {
  if (existing && typeof existing === 'string') {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SESSION_STORAGE_KEY, existing);
      } else {
        inMemorySessionId = existing;
      }
    } catch (error) {
      console.warn('CookieConsentProvider: unable to persist existing session id', error);
    }
    return existing;
  }

  if (typeof window === 'undefined') {
    if (!inMemorySessionId) {
      inMemorySessionId = createSessionId();
    }
    return inMemorySessionId;
  }

  try {
    const stored = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      return stored;
    }
  } catch (error) {
    console.warn('CookieConsentProvider: unable to read stored session id', error);
    if (!inMemorySessionId) {
      inMemorySessionId = createSessionId();
    }
    return inMemorySessionId;
  }

  const generated = createSessionId();
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, generated);
  } catch (error) {
    console.warn('CookieConsentProvider: unable to persist generated session id', error);
    inMemorySessionId = generated;
  }
  return generated;
}

function readStoredConsent() {
  const base = {
    ...defaultConsent,
    categories: { ...defaultConsent.categories },
    channels: [...DEFAULT_CHANNELS],
    legalBasis: [...DEFAULT_LEGAL_BASIS],
    scopes: [...defaultConsent.scopes],
    sessionId: ensureSessionId(defaultConsent.sessionId),
    record: null,
  };

  if (typeof window === 'undefined') {
    return base;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return base;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) {
      return base;
    }

    const normalizedCategories = normalizeCategories(parsed.categories);
    const storedScopes = normalizeStringArray(parsed.scopes);
    const scopes =
      storedScopes.length > 0
        ? mergeUniqueStrings(storedScopes, ['necessary'])
        : buildScopesFromCategories(normalizedCategories);

    const restored = {
      version: CONSENT_VERSION,
      categories: normalizedCategories,
      updatedAt: parsed.updatedAt || null,
      receiptId: parsed.receiptId || null,
      sessionId: ensureSessionId(parsed.sessionId),
      lastSyncedAt: parsed.lastSyncedAt || null,
      channels: mergeUniqueStrings(defaultConsent.channels, parsed.channels, DEFAULT_CHANNELS),
      legalBasis: mergeUniqueStrings(defaultConsent.legalBasis, parsed.legalBasis, DEFAULT_LEGAL_BASIS),
      scopes,
      record: null,
    };
    return normalizeConsentState(restored, base);
  } catch (error) {
    console.warn('CookieConsentProvider: unable to parse stored consent', error);
    return base;
  }
}

function persistConsent(consent) {
  if (typeof window === 'undefined') {
    return;
  }

  const payload = {
    version: CONSENT_VERSION,
    categories: normalizeCategories(consent.categories),
    updatedAt: consent.updatedAt || null,
    receiptId: consent.receiptId || null,
    sessionId: consent.sessionId || ensureSessionId(consent.sessionId),
    lastSyncedAt: consent.lastSyncedAt || null,
    channels: mergeUniqueStrings(consent.channels, [], DEFAULT_CHANNELS),
    legalBasis: mergeUniqueStrings(consent.legalBasis, [], DEFAULT_LEGAL_BASIS),
    scopes: mergeUniqueStrings(consent.scopes, buildScopesFromCategories(consent.categories)),
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.localStorage.setItem(SESSION_STORAGE_KEY, payload.sessionId);
  } catch (error) {
    console.warn('CookieConsentProvider: unable to persist consent locally', error);
  }
}

function parseDate(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function CookieConsentProvider({ children }) {
  const listenersRef = useRef(new Set());
  const queueServerSyncRef = useRef(null);
  const pendingSyncControllerRef = useRef(null);
  const fetchedFromServerRef = useRef(false);
  const [consent, setConsent] = useState(() => readStoredConsent());

  const notifyListeners = useCallback((categories) => {
    listenersRef.current.forEach((listener) => {
      try {
        listener(categories);
      } catch (error) {
        console.error('CookieConsentProvider: listener error', error);
      }
    });
  }, []);

  const getAccessToken = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.warn('CookieConsentProvider: unable to fetch auth session', error);
        return null;
      }
      return data?.session?.access_token || null;
    } catch (error) {
      console.warn('CookieConsentProvider: auth session lookup failed', error);
      return null;
    }
  }, []);

  const applyConsentUpdate = useCallback(
    (producer, { sync = false, syncOptions = undefined } = {}) => {
      let nextValue = null;
      let shouldSync = sync;
      let pendingSyncOptions = syncOptions;

      setConsent((previous) => {
        const result = producer(previous);
        if (!result) {
          nextValue = previous;
          shouldSync = false;
          pendingSyncOptions = undefined;
          return previous;
        }

        let candidate;
        let explicitNotify;

        if (Array.isArray(result)) {
          [candidate, explicitNotify = false] = result;
        } else {
          candidate = result;
          explicitNotify = undefined;
        }

        if (!candidate) {
          nextValue = previous;
          shouldSync = false;
          pendingSyncOptions = undefined;
          return previous;
        }

        const resolved = normalizeConsentState(candidate, previous);
        const shouldNotify =
          explicitNotify !== undefined
            ? Boolean(explicitNotify)
            : !areCategoriesEqual(previous.categories, resolved.categories);

        nextValue = resolved;
        persistConsent(resolved);

        if (shouldNotify) {
          notifyListeners(resolved.categories);
        }

        return resolved;
      });

      if (shouldSync && nextValue) {
        queueServerSyncRef.current?.(nextValue, pendingSyncOptions);
      }

      return nextValue;
    },
    [notifyListeners],
  );

  const queueServerSync = useCallback(
    (nextConsent, syncOptions = {}) => {
      if (typeof window === 'undefined' || !nextConsent?.sessionId) {
        return;
      }

      if (pendingSyncControllerRef.current) {
        pendingSyncControllerRef.current.abort();
      }

      const controller = new AbortController();
      pendingSyncControllerRef.current = controller;

      const payload = {
        sessionId: nextConsent.sessionId,
        policyVersion: nextConsent.version,
        categories: nextConsent.categories,
        receiptId: nextConsent.receiptId || undefined,
        channels: mergeUniqueStrings(nextConsent.channels, syncOptions.channels, DEFAULT_CHANNELS),
        legalBasis: mergeUniqueStrings(nextConsent.legalBasis, syncOptions.legalBasis, DEFAULT_LEGAL_BASIS),
        scopes: mergeUniqueStrings(nextConsent.scopes, syncOptions.scopes, DEFAULT_POLICY_SCOPES),
      };

      const metadataPayload = {};
      const syncMetadata =
        syncOptions?.metadata && typeof syncOptions.metadata === 'object'
          ? syncOptions.metadata
          : {};

      if (syncOptions?.source) {
        metadataPayload.source = syncOptions.source;
      }

      Object.entries(syncMetadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          metadataPayload[key] = value;
        }
      });

      if (Object.keys(metadataPayload).length > 0) {
        payload.metadata = metadataPayload;
      }

      (async () => {
        try {
          const headers = {
            'Content-Type': 'application/json',
          };

          const token = await getAccessToken();
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }

          const response = await fetch(SERVER_ENDPOINT, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
            signal: controller.signal,
          });

          if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`Server responded with ${response.status} ${text}`);
          }

          const data = await response.json().catch(() => ({}));
          const serverRecord = data?.record || null;

          applyConsentUpdate(
            (previous) => {
              if (previous.sessionId !== nextConsent.sessionId) {
                return null;
              }

              const receiptId = data?.receiptId || previous.receiptId || null;
              const storedAt =
                data?.storedAt ||
                serverRecord?.acceptedAt ||
                serverRecord?.updatedAt ||
                new Date().toISOString();
              const serverCategories = data?.categories
                ? normalizeCategories(data.categories)
                : previous.categories;
              const categoriesChanged = !areCategoriesEqual(previous.categories, serverCategories);

              const channelsFromServer = serverRecord?.channels || [];
              const legalBasisFromServer = serverRecord?.legalBasis || [];
              const scopesFromServer = serverRecord?.scopes || [];

              const mergedChannels = mergeUniqueStrings(
                previous.channels,
                channelsFromServer,
                payload.channels,
              );

              const mergedLegalBasis = mergeUniqueStrings(
                previous.legalBasis,
                legalBasisFromServer,
                payload.legalBasis,
              );

              const mergedScopes = mergeUniqueStrings(
                previous.scopes,
                scopesFromServer,
                buildScopesFromCategories(serverCategories),
              );

              const updatedAtCandidate = serverRecord?.updatedAt || (categoriesChanged ? storedAt : null);

              return [
                {
                  ...previous,
                  categories: categoriesChanged ? serverCategories : previous.categories,
                  updatedAt: updatedAtCandidate || previous.updatedAt,
                  receiptId,
                  lastSyncedAt: storedAt,
                  channels: mergedChannels,
                  legalBasis: mergedLegalBasis,
                  scopes: mergedScopes,
                  record: serverRecord || previous.record,
                },
                categoriesChanged,
              ];
            },
            { sync: false },
          );
        } catch (error) {
          if (error.name === 'AbortError') {
            return;
          }
          console.error('CookieConsentProvider: failed to sync consent', error);
        } finally {
          if (pendingSyncControllerRef.current === controller) {
            pendingSyncControllerRef.current = null;
          }
        }
      })();
    },
    [applyConsentUpdate, getAccessToken],
  );

  useEffect(() => {
    queueServerSyncRef.current = queueServerSync;
    return () => {
      queueServerSyncRef.current = null;
    };
  }, [queueServerSync]);

  const setCategories = useCallback(
    (updater, options = {}) => {
      const forceSync = Boolean(options?.forceSync);
      const normalizedOptions = {
        channels: normalizeStringArray(options?.channels),
        legalBasis: normalizeStringArray(options?.legalBasis),
        metadata:
          options?.metadata && typeof options.metadata === 'object' ? options.metadata : {},
        source: options?.source || DEFAULT_SYNC_SOURCE,
      };

      applyConsentUpdate(
        (previous) => {
          const nextCategories = normalizeCategories(
            typeof updater === 'function' ? updater(previous.categories) : updater,
          );
          const categoriesAreEqual = areCategoriesEqual(previous.categories, nextCategories);

          const nextChannels = mergeUniqueStrings(previous.channels, normalizedOptions.channels, DEFAULT_CHANNELS);
          const channelsChanged = !areStringArraysEqual(previous.channels, nextChannels);

          const nextLegalBasis = mergeUniqueStrings(
            previous.legalBasis,
            normalizedOptions.legalBasis,
            DEFAULT_LEGAL_BASIS,
          );
          const legalBasisChanged = !areStringArraysEqual(previous.legalBasis, nextLegalBasis);

          const nextScopes = mergeUniqueStrings(
            previous.scopes,
            buildScopesFromCategories(nextCategories),
          );
          const scopesChanged = !areStringArraysEqual(previous.scopes, nextScopes);

          const ensuredSessionId = ensureSessionId(previous.sessionId);
          const sessionChanged = ensuredSessionId !== previous.sessionId;

          if (
            !forceSync &&
            categoriesAreEqual &&
            !channelsChanged &&
            !legalBasisChanged &&
            !scopesChanged &&
            !sessionChanged &&
            previous.updatedAt !== null
          ) {
            return null;
          }

          const nextUpdatedAt =
            !categoriesAreEqual || forceSync ? new Date().toISOString() : previous.updatedAt;

          return [
            {
              ...previous,
              categories: nextCategories,
              updatedAt: nextUpdatedAt,
              sessionId: ensuredSessionId,
              channels: nextChannels,
              legalBasis: nextLegalBasis,
              scopes: nextScopes,
            },
            forceSync || !categoriesAreEqual,
          ];
        },
        { sync: true, syncOptions: normalizedOptions },
      );
    },
    [applyConsentUpdate],
  );

  const updateConsent = useCallback(
    (partialCategories, options = {}) => {
      if (!partialCategories || typeof partialCategories !== 'object') {
        return;
      }

      setCategories(
        (current) => ({
          ...current,
          ...partialCategories,
        }),
        options,
      );
    },
    [setCategories],
  );

  const resetConsent = useCallback(() => {
    setCategories(defaultConsent.categories, {
      channels: DEFAULT_CHANNELS,
      legalBasis: DEFAULT_LEGAL_BASIS,
      source: 'cookie_preferences_reset',
    });
  }, [setCategories]);

  const onConsentChange = useCallback((listener) => {
    if (typeof listener !== 'function') {
      return () => {};
    }

    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const loadRemoteConsent = useCallback(async () => {
    if (typeof window === 'undefined' || !consent.sessionId || fetchedFromServerRef.current) {
      return;
    }

    fetchedFromServerRef.current = true;

    try {
      const params = new URLSearchParams({
        sessionId: consent.sessionId,
        policyVersion: CONSENT_VERSION,
      });

      const headers = {};
      const token = await getAccessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${SERVER_ENDPOINT}?${params.toString()}`, { headers });

      if (response.status === 404 || response.status === 204) {
        return;
      }

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Server responded with ${response.status} ${text}`);
      }

      const data = await response.json().catch(() => null);
      if (!data) {
        return;
      }

      const remoteCategories = Array.isArray(data.categories)
        ? categoriesObjectFromList(data.categories)
        : normalizeCategories(data.categories);

      const remoteScopes = Array.isArray(data.scopes) ? data.scopes : normalizeStringArray(data.scopes);
      const remoteChannels = normalizeStringArray(data.channels);
      const remoteLegalBasis = normalizeStringArray(data.legalBasis);
      const remoteAcceptedAt = data.acceptedAt || data.updatedAt || null;
      const remoteReceiptId = data.receiptId || null;
      const lastSyncedAt = data.storedAt || remoteAcceptedAt || null;

      applyConsentUpdate(
        (previous) => {
          const remoteDate = parseDate(remoteAcceptedAt);
          const localDate = parseDate(previous.updatedAt);
          const isRemoteNewer =
            remoteDate && (!localDate || remoteDate.getTime() > localDate.getTime());

          const shouldApplyCategories =
            isRemoteNewer && !areCategoriesEqual(previous.categories, remoteCategories);

          const shouldApplyReceipt = remoteReceiptId && !previous.receiptId;

          const mergedChannels = mergeUniqueStrings(
            previous.channels,
            remoteChannels,
            DEFAULT_CHANNELS,
          );
          const channelsChanged = !areStringArraysEqual(previous.channels, mergedChannels);

          const mergedLegalBasis = mergeUniqueStrings(
            previous.legalBasis,
            remoteLegalBasis,
            DEFAULT_LEGAL_BASIS,
          );
          const legalBasisChanged = !areStringArraysEqual(previous.legalBasis, mergedLegalBasis);

          const mergedScopes = mergeUniqueStrings(
            previous.scopes,
            remoteScopes,
            buildScopesFromCategories(remoteCategories),
          );
          const scopesChanged = !areStringArraysEqual(previous.scopes, mergedScopes);

          const syncTimestampChanged =
            lastSyncedAt && lastSyncedAt !== previous.lastSyncedAt;

          if (
            !shouldApplyCategories &&
            !shouldApplyReceipt &&
            !channelsChanged &&
            !legalBasisChanged &&
            !scopesChanged &&
            !syncTimestampChanged
          ) {
            return null;
          }

          return [
            {
              ...previous,
              version: data.policyVersion || previous.version,
              categories: shouldApplyCategories ? remoteCategories : previous.categories,
              updatedAt: shouldApplyCategories ? remoteAcceptedAt || previous.updatedAt : previous.updatedAt,
              receiptId: shouldApplyReceipt ? remoteReceiptId : previous.receiptId,
              lastSyncedAt: lastSyncedAt || previous.lastSyncedAt,
              channels: mergedChannels,
              legalBasis: mergedLegalBasis,
              scopes: mergedScopes,
              record: data || previous.record,
            },
            shouldApplyCategories,
          ];
        },
        { sync: false },
      );
    } catch (error) {
      console.warn('CookieConsentProvider: unable to load remote consent', error);
    }
  }, [applyConsentUpdate, consent.sessionId, getAccessToken]);

  useEffect(() => {
    loadRemoteConsent();
  }, [loadRemoteConsent]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handleStorage = (event) => {
      if (event.key && event.key !== STORAGE_KEY && event.key !== SESSION_STORAGE_KEY) {
        return;
      }

      const next = readStoredConsent();
      setConsent((previous) => {
        const categoriesChanged = !areCategoriesEqual(previous.categories, next.categories);
        const receiptChanged = previous.receiptId !== next.receiptId;
        const updatedAtChanged = previous.updatedAt !== next.updatedAt;
        const channelsChanged = !areStringArraysEqual(previous.channels, next.channels);
        const legalBasisChanged = !areStringArraysEqual(previous.legalBasis, next.legalBasis);
        const scopesChanged = !areStringArraysEqual(previous.scopes, next.scopes);
        const lastSyncChanged = previous.lastSyncedAt !== next.lastSyncedAt;

        if (
          !categoriesChanged &&
          !receiptChanged &&
          !updatedAtChanged &&
          !channelsChanged &&
          !legalBasisChanged &&
          !scopesChanged &&
          !lastSyncChanged &&
          previous.sessionId === next.sessionId
        ) {
          return previous;
        }

        if (categoriesChanged) {
          notifyListeners(next.categories);
        }

        return next;
      });
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [notifyListeners]);

  const syncConsent = useCallback(() => {
    queueServerSync(consent, { source: DEFAULT_SYNC_SOURCE });
  }, [consent, queueServerSync]);

  const value = useMemo(
    () => ({
      version: consent.version,
      categories: consent.categories,
      updatedAt: consent.updatedAt,
      receiptId: consent.receiptId,
      sessionId: consent.sessionId,
      lastSyncedAt: consent.lastSyncedAt,
      channels: consent.channels,
      legalBasis: consent.legalBasis,
      scopes: consent.scopes,
      record: consent.record,
      hasConsent: (category) => Boolean(consent.categories?.[category]),
      updateConsent,
      resetConsent,
      onConsentChange,
      syncConsent,
    }),
    [consent, onConsentChange, resetConsent, syncConsent, updateConsent],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}

export { CONSENT_VERSION as COOKIE_CONSENT_VERSION };


